import type { Athlete, Course, UserProfile, GroupRideDetails } from '../types';
import { calculateLapsForAthlete } from '../services/raceAnalysis';
import { 
  formatSectorTime, 
  SectorAthleteTelemetry, 
  SectorLeaderboardRow 
} from './raceSector';

export interface AthleteRaceContext {
  name: string;
  category: string;
  date: string;
  course: Course;
  athletes: Athlete[];
}

export interface AthleteRankContext {
  rank: number;
  totalAthletes: number;
  timeBehindMs?: number;
  lapsDown?: number;
}

export const generateAthletePrompt = (
    athlete: Athlete, 
    raceData: AthleteRaceContext, 
    profile?: UserProfile | null,
    rankInfo?: AthleteRankContext
): string => {
    const { track } = athlete;
    const laps = calculateLapsForAthlete(track, raceData.course.start);

    let maxSpeed = 0, totalSpeed = 0;
    let maxPower = 0, totalPower = 0, powerCount = 0;
    let maxHr = 0, totalHr = 0, hrCount = 0;

    track.forEach(p => {
        if (p.speed > maxSpeed) maxSpeed = p.speed;
        totalSpeed += p.speed;
        if (p.power !== null && p.power !== undefined) {
            if (p.power > maxPower) maxPower = p.power;
            totalPower += p.power;
            powerCount++;
        }
        if (p.heartRate !== null && p.heartRate !== undefined && p.heartRate > 0) {
            if (p.heartRate > maxHr) maxHr = p.heartRate;
            totalHr += p.heartRate;
            hrCount++;
        }
    });

    const avgSpeedMph = track.length > 0 ? (totalSpeed / track.length) * 0.621371 : 0;
    const avgPower = powerCount > 0 ? totalPower / powerCount : 0;
    const avgHr = hrCount > 0 ? Math.round(totalHr / hrCount) : null;
    const weightKg = athlete.weight || (profile?.weightLbs ? profile.weightLbs / 2.20462 : null);
    const avgWkg = weightKg && avgPower > 0 ? (avgPower / weightKg).toFixed(1) : null;
    const maxWkg = weightKg && maxPower > 0 ? (maxPower / weightKg).toFixed(1) : null;

    let lapDataString = "No completed laps recorded.\n";
    if (laps.length > 0) {
        lapDataString = laps.map(lap => {
            const timeMin = (lap.lapTime / 1000 / 60).toFixed(1);
            const speedMph = (lap.avgSpeed * 0.621371).toFixed(1);
            const pwrStr = lap.avgPower ? `${Math.round(lap.avgPower)}W` : 'N/A';
            const wkgStr = weightKg && lap.avgPower ? ` (${(lap.avgPower / weightKg).toFixed(1)} W/kg)` : '';
            const hrStr = lap.avgHeartRate ? ` | Avg HR: ${Math.round(lap.avgHeartRate)} bpm` : '';
            return `- Lap ${lap.lapNumber}: Time: ${timeMin} min | Avg Speed: ${speedMph} mph | Avg Power: ${pwrStr}${wkgStr}${hrStr}`;
        }).join('\n');
    }

    const matchEvents = athlete.matchEvents || [];
    const capacity = profile?.estimatedMatches || ((profile?.racesAnalyzedForMatches || 0) < 5 ? 4 : 'Unknown');
    const raceStartTime = track[0]?.timestamp || 0;

    let matchString = "No deep anaerobic matches burned recorded.\n";
    if (matchEvents.length > 0) {
        matchString = matchEvents.map((m, i) => {
            const minIntoRace = raceStartTime ? Math.max(0, Math.round((m.time - raceStartTime) / 60000)) : null;
            const timing = minIntoRace !== null ? `[~Min ${minIntoRace}] ` : '';
            const wkgStr = weightKg && m.avgPower ? ` (${(m.avgPower / weightKg).toFixed(1)} W/kg)` : '';
            return `- Match ${i + 1}: ${timing}${Math.round(m.durationSeconds)}s @ ${Math.round(m.avgPower)}W${wkgStr}`;
        }).join('\n');
    }

    let resultString = 'Individual Activity';
    if (rankInfo) {
        const gap = rankInfo.lapsDown && rankInfo.lapsDown > 0
            ? `(${rankInfo.lapsDown} lap${rankInfo.lapsDown > 1 ? 's' : ''} down)`
            : rankInfo.timeBehindMs && rankInfo.timeBehindMs > 0
                ? `(+${(rankInfo.timeBehindMs / 1000).toFixed(1)}s behind leader)`
                : rankInfo.rank === 1 ? '(Leader / Winner)' : '';
        resultString = `Finished P${rankInfo.rank} of ${rankInfo.totalAthletes} riders ${gap}`.trim();
    }

    return `You are a World-Tour level cycling director and performance physiologist analyzing race telemetry.

Provide an authoritative, concise, data-grounded post-race debrief for athlete **${athlete.name}**.

### Athlete & Race Context
- **Event:** ${raceData.name} (${raceData.category}) - ${raceData.date}
- **Finish Outcome:** ${resultString}
- **Rider Weight:** ${weightKg ? `${weightKg.toFixed(1)} kg` : 'Unknown'}
- **Functional Threshold Power (FTP):** ${profile?.ftp ? `${profile.ftp} W` : 'Unknown'}

### Physiological & Power Metrics
- **Speed:** Avg ${avgSpeedMph.toFixed(1)} mph | Max ${(maxSpeed * 0.621371).toFixed(1)} mph
- **Power:** Avg ${Math.round(avgPower)} W ${avgWkg ? `(${avgWkg} W/kg)` : ''} | Max ${Math.round(maxPower)} W ${maxWkg ? `(${maxWkg} W/kg)` : ''}
${profile?.ftp && avgPower ? `- **Intensity Factor (IF):** ${(avgPower / profile.ftp).toFixed(2)}` : ''}
- **Heart Rate:** Avg ${avgHr ? `${avgHr} bpm` : 'No HR data'} | Max ${maxHr ? `${maxHr} bpm` : 'N/A'}

### Lap-by-Lap Progression
${lapDataString}

### Tactical Matches Burned (Anaerobic Reserve: ${matchEvents.length} / ${capacity} capacity)
${matchString}
*(Note: A "match" is a hard effort significantly above threshold that drains W' anaerobic battery).*

---

### Instructions for Analysis:
Analyze the data with elite tactical realism. Ground all observations strictly in the telemetry provided (do not invent unrecorded hill climbs or mechanicals unless clearly implied by speed/power data). Provide your assessment in clear Markdown with the following structure:

1. **Executive Debrief (2-3 sentences):** High-level verdict on their race execution, pacing efficiency, and outcome.
2. **Pacing & Power Sustainability:** Analyze power stability across laps. Did they fade (positive split), surge erratically, or pace evenly? Note any heart rate decoupling or aerobic strain.
3. **Match Management & Tactical Timing:** How effectively did they invest their matches? Were matches burned early (defending/bridging) or conserved for key moments?
4. **Coach's Prescriptions for Next Race:** 2 specific, actionable tactical or training takeaways (e.g., draft conservation, specific anaerobic interval work, pacing discipline).
`;
};

export const generateGroupRidePrompt = (
    athlete: Athlete, 
    raceData: AthleteRaceContext, 
    groupRideDetails?: GroupRideDetails,
    sectorTelemetry?: SectorAthleteTelemetry | null,
    sectorRow?: SectorLeaderboardRow,
    totalSectorContenders?: number,
    sectorDistanceMiles?: number | null,
    sectorElevationGainFt?: number | null,
    profile?: UserProfile | null
): string => {
    const { track } = athlete;
    const weightKg = athlete.weight || (profile?.weightLbs ? profile.weightLbs / 2.20462 : null);

    let maxSpeed = 0, totalSpeed = 0;
    let maxPower = 0, totalPower = 0, powerCount = 0;
    let maxHr = 0, totalHr = 0, hrCount = 0;

    track.forEach(p => {
        if (p.speed > maxSpeed) maxSpeed = p.speed;
        totalSpeed += p.speed;
        if (p.power !== null && p.power !== undefined) {
            if (p.power > maxPower) maxPower = p.power;
            totalPower += p.power;
            powerCount++;
        }
        if (p.heartRate !== null && p.heartRate !== undefined && p.heartRate > 0) {
            if (p.heartRate > maxHr) maxHr = p.heartRate;
            totalHr += p.heartRate;
            hrCount++;
        }
    });

    const avgSpeedMph = track.length > 0 ? (totalSpeed / track.length) * 0.621371 : 0;
    const avgPower = powerCount > 0 ? totalPower / powerCount : 0;
    const avgHr = hrCount > 0 ? Math.round(totalHr / hrCount) : null;
    const avgWkg = weightKg && avgPower > 0 ? (avgPower / weightKg).toFixed(1) : null;
    const maxWkg = weightKg && maxPower > 0 ? (maxPower / weightKg).toFixed(1) : null;

    const matchEvents = athlete.matchEvents || [];
    const capacity = profile?.estimatedMatches || ((profile?.racesAnalyzedForMatches || 0) < 5 ? 4 : 'Unknown');

    const totalRideDistanceM = track.length > 1 ? Math.max(0, track[track.length - 1].distance - track[0].distance) : 0;
    const totalRideDistanceMiles = totalRideDistanceM > 0 ? (totalRideDistanceM * 0.000621371).toFixed(1) : 'Unknown';
    const totalDurationMs = track.length > 1 ? Math.max(0, track[track.length - 1].timestamp - track[0].timestamp) : 0;
    const totalDurationStr = formatSectorTime(totalDurationMs);

    const hasSector = !!sectorTelemetry;
    let sectorSection = '';

    if (hasSector && sectorTelemetry) {
        const effort = sectorTelemetry.effort;
        const timeStr = formatSectorTime(effort.elapsedMs);
        const sectorAvgSpeed = (effort.avgSpeedMps * 2.23694).toFixed(1);
        const sectorPwr = effort.avgPower ? `${Math.round(effort.avgPower)} W` : 'N/A';
        const sectorMaxPwr = sectorTelemetry.maxPower ? `${Math.round(sectorTelemetry.maxPower)} W` : 'N/A';
        const sectorWkg = sectorTelemetry.sectorAvgWkg ? `${sectorTelemetry.sectorAvgWkg} W/kg` : 'N/A';
        const sectorMaxWkg = sectorTelemetry.sectorMaxWkg ? `${sectorTelemetry.sectorMaxWkg} W/kg` : 'N/A';
        const sectorHr = effort.avgHr ? `${Math.round(effort.avgHr)} bpm` : 'N/A';
        const rankStr = sectorRow 
            ? `P${sectorRow.rank} of ${totalSectorContenders || sectorRow.rank} riders ${sectorRow.gapMs > 0 ? `(+${(sectorRow.gapMs / 1000).toFixed(1)}s behind sector winner)` : '(Sector Winner / First Wheel)'}`
            : 'Contested';

        const sectorDistStr = sectorDistanceMiles ? `${sectorDistanceMiles} mi` : `${(effort.distanceM * 0.000621371).toFixed(2)} mi`;
        const elevStr = sectorElevationGainFt ? ` | Elevation Gain: +${sectorElevationGainFt} ft` : '';

        sectorSection = `### ⚡ The Race Sector (Spirited Segment Performance)
- **Sector Distance:** ${sectorDistStr}${elevStr}
- **Sector Placement:** ${rankStr}
- **Sector Elapsed Time:** ${timeStr}
- **Speed in Sector:** Avg ${sectorAvgSpeed} mph | Max ${sectorTelemetry.maxSpeedMph} mph
- **Power in Sector:** Avg ${sectorPwr} (${sectorWkg}) | Max ${sectorMaxPwr} (${sectorMaxWkg})
${profile?.ftp && effort.avgPower ? `- **Sector Intensity Factor (IF):** ${(effort.avgPower / profile.ftp).toFixed(2)}` : ''}
- **Heart Rate in Sector:** Avg ${sectorHr}
- **Matches Burned in Sector:** ${sectorTelemetry.sectorMatches.length} match(es) exploded inside the sector

### Neutral Rollout & Energy Conservation
- **Rollout Power (Pre-Sector):** ${sectorTelemetry.preSectorAvgPower ? `Avg ${sectorTelemetry.preSectorAvgPower} W` : 'Moderate'}
- **Matches Burned Pre-Sector:** ${sectorTelemetry.preSectorMatches.length} match(es) burned prior to the race sector (battery conservation check)
- **Matches Burned Post-Sector:** ${sectorTelemetry.postSectorMatches.length} match(es) burned during regroup/return
`;
    }

    const dropStatus = groupRideDetails?.isDrop 
        ? 'Drop Ride (aggressive, non-wait pace)' 
        : 'No-Drop Ride with scheduled regroup points';

    const rideDescription = groupRideDetails?.infoText 
        ? groupRideDetails.infoText.trim() 
        : 'No host description provided.';

    return `You are an elite cycling coach, performance physiologist, and seasoned group ride tactician.
Provide an authoritative, punchy, data-grounded post-ride debrief for athlete **${athlete.name}** after their group ride.

### Group Ride & Route Context
- **Group Ride:** ${raceData.name} - ${raceData.date}
- **Ride Format:** ${dropStatus}
- **Location:** ${groupRideDetails?.locationName || 'Local Route'}
- **Meetup / Rollout:** ${groupRideDetails?.meetupTime || 'Morning'} ${groupRideDetails?.rollTime ? `(Rolls at ${groupRideDetails.rollTime})` : ''}
- **Host Ride Description & Route Rules:**
"${rideDescription}"

### Athlete Baseline
- **Rider Weight:** ${weightKg ? `${weightKg.toFixed(1)} kg` : 'Unknown'}
- **Functional Threshold Power (FTP):** ${profile?.ftp ? `${profile.ftp} W` : 'Unknown'}
- **Anaerobic Match Capacity:** ${capacity} matches (total burned across whole ride: ${matchEvents.length})

### Entire Ride Overview (Rollout, Regroups & Cooldown included)
- **Total Distance:** ${totalRideDistanceMiles} miles | **Total Time:** ${totalDurationStr}
- **Overall Speed:** Avg ${avgSpeedMph.toFixed(1)} mph | Max ${(maxSpeed * 0.621371).toFixed(1)} mph
- **Overall Power:** Avg ${Math.round(avgPower)} W ${avgWkg ? `(${avgWkg} W/kg)` : ''} | Max ${Math.round(maxPower)} W ${maxWkg ? `(${maxWkg} W/kg)` : ''}
${profile?.ftp && avgPower ? `- **Overall Intensity Factor (IF):** ${(avgPower / profile.ftp).toFixed(2)}` : ''}
- **Overall Heart Rate:** Avg ${avgHr ? `${avgHr} bpm` : 'N/A'} | Max ${maxHr ? `${maxHr} bpm` : 'N/A'}

${sectorSection}
---

### Instructions for Analysis:
Analyze the rider's group ride with tactical realism. Account for the ride's format (${dropStatus}) and route write-up. Understand that group rides include neutral rollouts, easy drafting, and regroup points, so DO NOT penalize the rider for low average power across the entire ride. Instead, focus heavily on their **energy conservation before the hammer dropped** and their **power delivery during the contested Race Sector**.

Provide your analysis in clear Markdown with this structure:

1. **Executive Ride Debrief (2-3 sentences):** High-level verdict on their ride execution, how they handled the pack, and their race sector outcome.
2. **⚡ Race Sector Deep Dive:** Analyze their performance on the spirited segment. Break down their power sustainability, peak watts/W-kg, speed, and whether they jumped too early, blew up, or paced the effort cleanly to the line.
3. **Drafting & Energy Conservation:** Evaluate how well they preserved their anaerobic battery in the neutral zone before the sector. Did they waste matches needlessly early on, or stay sheltered in the wheels?
4. **Coach's Blueprint for Next Week:** 2 specific, high-leverage tactical or training takeaways tailored to this specific group ride (e.g. positioning before the sector gate, pacing discipline, or explosive sprint timing).
`;
};
